/**
 * Meeting detail — the statutory lifecycle, one action per stage:
 * DRAFT → Issue notice (validates clear days, pushes members)
 * NOTICED → Mark held (MC: tick committee attendance; AGM/SGM: head-count)
 *           → quorum verdict, recorded PROVISIONALLY
 * HELD → Record minutes (Secretary)
 * MINUTES_DRAFT → Certify (a DIFFERENT member signs off attendance + quorum +
 *   minutes in one step — bye-law 136; that is the second-person approval).
 * Buttons appear from the caller's bits on governance.meetings; the server
 * re-checks everything and answers refusals with the bye-law citation.
 */
import { useLocalSearchParams } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, EmptyState, Field, Input, Pill, Screen, SectionLabel,
  Sheet, Skeleton, TopBar,
} from '@/ui';

import { fmtMeetingWhen, STATUS_META } from './index';

type CommitteeRow = { id: number; fullName: string; present: boolean | null };
type Detail = {
  id: number; kind: 'MC' | 'AGM' | 'SGM'; title: string; status: string;
  agenda: { text: string; recusal: boolean }[];
  scheduledAt: string | null; venue: string;
  noticeIssuedAt: string | null; clearDaysNow?: number; clearDaysGiven?: number;
  quorumRequired: number | null; presentCount: number | null; quorumMet: boolean | null;
  heldByName: string | null; attendanceCertified: boolean;
  minutes: string; minutesByName: string | null;
  minutesConfirmedByName: string | null; minutesConfirmedAt: string | null;
  rule?: { label: string; noticeDays: number; noticeCitation: string; quorumCitation: string; cadence: string };
  committee?: CommitteeRow[];
};

const KIND_KEY: Record<string, DictKey> = { MC: 'mtg_kind_mc', AGM: 'mtg_kind_agm', SGM: 'mtg_kind_sgm' };

export default function MeetingDetailScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [m, setM] = React.useState<Detail | null>(null);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [bits, setBits] = React.useState<string[]>([]);
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [heldOpen, setHeldOpen] = React.useState(false);
  const [present, setPresent] = React.useState<Set<number>>(new Set());
  const [headCount, setHeadCount] = React.useState('');
  const [minutesOpen, setMinutesOpen] = React.useState(false);
  const [minutesText, setMinutesText] = React.useState('');
  const [certifyOpen, setCertifyOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const [d, me] = await Promise.all([
        api.get<Detail>(`/api/meetings/${id}`),
        api.get<{ perms?: Record<string, string[]> }>('/api/positions/me').catch(() => ({ perms: {} as Record<string, string[]> })),
      ]);
      setM(d);
      setBits(me.perms?.['governance.meetings'] ?? []);
      setState('ready');
    } catch {
      setState('error');
    }
  }, [id]);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const act = (path: string, body?: object, okMsg?: string) => {
    setBusy(true);
    setNotice(null);
    api.post<{ meeting?: Detail & { warning?: string } }>(`/api/meetings/${id}/${path}`, body ?? {})
      .then((res) => {
        const warning = res.meeting?.warning;
        setNotice(warning ? { kind: 'err', text: warning } : { kind: 'ok', text: okMsg ?? t('mtd_done') });
        void load();
      })
      .catch((e) => {
        const er = e as { error?: string; citation?: string };
        setNotice({ kind: 'err', text: er?.error ? (er.citation ? `${er.error} — ${er.citation}` : er.error) : t('mtd_err_generic') });
      })
      .finally(() => { setBusy(false); setHeldOpen(false); setMinutesOpen(false); });
  };

  if (state === 'loading' || !m) {
    return (
      <Screen scroll={false}>
        <TopBar title={t('mtd_title_meeting')} onBack={goBack} />
        <Screen padded style={{ paddingTop: space.s4 }}>
          {state === 'error' ? (
            <EmptyState icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
              title={t('mtd_err_load')} cta={t('retry')} onCta={() => { void load(); }} />
          ) : <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={84} />)}</View>}
        </Screen>
      </Screen>
    );
  }

  const st = STATUS_META[m.status];
  const stLabel = st ? t(st.labelKey) : m.status;
  const canWrite = bits.includes('write');
  const canEdit = bits.includes('edit');
  const canApprove = bits.includes('approve');

  return (
    <Screen scroll={false}>
      <TopBar title={KIND_KEY[m.kind] ? t(KIND_KEY[m.kind]) : (m.rule?.label ?? t('mtd_title_meeting'))} onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: space.s2 }}>
          <AppText variant="headingMd" weight="600" style={{ flex: 1 }}>{m.title}</AppText>
          <Pill status={st?.s ?? 'pending'} label={stLabel} />
        </View>
        <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
          {fmtMeetingWhen(m.scheduledAt)} · {m.venue}
        </AppText>

        {/* Notice status */}
        <Card style={{ marginTop: space.s4 }}>
          <SectionLabel>{t('mtd_notice')}</SectionLabel>
          {m.noticeIssuedAt ? (
            <AppText variant="bodyMd" style={{ marginTop: space.s2 }}>
              Issued with {m.clearDaysGiven} clear days — rule asks {m.rule?.noticeDays}.
            </AppText>
          ) : (
            <AppText variant="bodyMd" style={{ marginTop: space.s2 }}>
              Not issued yet{typeof m.clearDaysNow === 'number'
                ? ` — ${m.clearDaysNow} clear days remain before the meeting (needs ${m.rule?.noticeDays}).` : '.'}
            </AppText>
          )}
          <View style={{ flexDirection: 'row', gap: 6, marginTop: space.s2, alignItems: 'flex-start' }}>
            <Icon name="lock" size={12} color={colors.accent700} />
            <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>{m.rule?.noticeCitation}</AppText>
          </View>
        </Card>

        {/* Agenda */}
        <Card style={{ marginTop: space.s3 }}>
          <SectionLabel>{t('mtd_business')}</SectionLabel>
          {m.agenda.map((a, i) => (
            <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s2 }}>
              <AppText variant="bodySm" tone="tertiary" mono>{i + 1}.</AppText>
              <AppText variant="bodyMd" style={{ flex: 1 }}>{a.text}</AppText>
              {a.recusal ? <Pill status="urgent" label={t('mtd_recusal')} /> : null}
            </View>
          ))}
        </Card>

        {/* Quorum */}
        <Card style={{ marginTop: space.s3 }}>
          <SectionLabel>{t('mtd_quorum')}</SectionLabel>
          <AppText variant="bodyMd" style={{ marginTop: space.s2 }}>
            {m.quorumMet == null
              ? t('mtd_required_present', { n: m.quorumRequired ?? '—' })
              : t('mtd_present_quorum', { p: m.presentCount ?? 0, r: m.quorumRequired ?? 0, verdict: m.quorumMet ? t('mtd_met') : t('mtd_not_met') })}
          </AppText>
          <View style={{ flexDirection: 'row', gap: 6, marginTop: space.s2, alignItems: 'flex-start' }}>
            <Icon name="lock" size={12} color={colors.accent700} />
            <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>{m.rule?.quorumCitation}</AppText>
          </View>
          {m.committee && m.status !== 'DRAFT' && m.status !== 'NOTICED' ? (
            <View style={{ marginTop: space.s3 }}>
              {m.committee.map((c) => (
                <View key={c.id} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2, paddingVertical: 4 }}>
                  <Icon name={c.present ? 'check' : 'x'} size={15}
                    color={c.present ? colors.primary700 : colors.textTertiary} />
                  <AppText variant="bodySm">{c.fullName}</AppText>
                </View>
              ))}
            </View>
          ) : null}
          {/* Attendance is provisional until a second member certifies it with
              the minutes (bye-law 136) — surface that state clearly. */}
          {m.quorumMet != null ? (
            m.attendanceCertified ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: space.s3 }}>
                <Icon name="check" size={14} color={colors.primary700} />
                <AppText variant="bodySm" weight="500" style={{ color: colors.primary700 }}>
                  {t('mtd_certified_by', { name: m.minutesConfirmedByName ?? t('mtd_the_committee') })}
                  {m.heldByName ? t('mtd_recorded_suffix', { name: m.heldByName }) : ''}
                </AppText>
              </View>
            ) : (
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6, marginTop: space.s3 }}>
                <Icon name="clock" size={13} color={colors.accent700} />
                <AppText variant="bodySm" style={{ color: colors.accent700, flex: 1 }}>
                  Provisional{m.heldByName ? ` — recorded by ${m.heldByName}` : ''}. A second member
                  certifies attendance and quorum when the minutes are confirmed.
                </AppText>
              </View>
            )
          ) : null}
        </Card>

        {/* Minutes */}
        {m.minutes || m.status === 'HELD' ? (
          <Card style={{ marginTop: space.s3 }}>
            <SectionLabel>{t('mtd_minutes')}</SectionLabel>
            {m.minutes ? (
              <>
                <AppText variant="bodyMd" style={{ marginTop: space.s2 }}>{m.minutes}</AppText>
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
                  Recorded by {m.minutesByName ?? '—'}
                  {m.minutesConfirmedByName
                    ? ` · certified by ${m.minutesConfirmedByName} (bye-law 136)` : ' · awaiting a second member to certify'}
                </AppText>
              </>
            ) : (
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
                {t('mtd_not_recorded')}
              </AppText>
            )}
          </Card>
        ) : null}

        {notice ? (
          <AppText variant="bodySm" weight="500"
            style={{ marginTop: space.s3, color: notice.kind === 'ok' ? colors.primary700 : colors.danger500 }}>
            {notice.text}
          </AppText>
        ) : null}

        {/* Stage actions */}
        <View style={{ marginTop: space.s4, gap: space.s2, marginBottom: space.s6 }}>
          {m.status === 'DRAFT' && canWrite ? (
            <Button title={busy ? t('mtd_working') : t('mtd_issue_notice')} disabled={busy}
              onPress={() => act('notice', {}, t('mtd_ok_notice'))} />
          ) : null}
          {(m.status === 'NOTICED' || m.status === 'DRAFT') && canEdit ? (
            <Button title={t('mtd_mark_held')} kind={m.status === 'NOTICED' ? 'primary' : 'secondary'}
              onPress={() => {
                setPresent(new Set((m.committee ?? []).filter((c) => c.present).map((c) => c.id)));
                setHeadCount('');
                setHeldOpen(true);
              }} />
          ) : null}
          {m.status === 'HELD' && canEdit ? (
            <Button title={t('mtd_record_minutes')} onPress={() => { setMinutesText(m.minutes || ''); setMinutesOpen(true); }} />
          ) : null}
          {m.status === 'MINUTES_DRAFT' && canEdit ? (
            <Button title={t('mtd_edit_minutes')} kind="secondary"
              onPress={() => { setMinutesText(m.minutes || ''); setMinutesOpen(true); }} />
          ) : null}
          {m.status === 'MINUTES_DRAFT' && canApprove ? (
            <Button title={t('mtd_certify_meeting')} disabled={busy} onPress={() => setCertifyOpen(true)} />
          ) : null}
          {(m.status === 'DRAFT' || m.status === 'NOTICED') && canEdit ? (
            <Button title={t('mtd_cancel_meeting')} kind="tertiary"
              onPress={() => act('cancel', {}, t('mtd_ok_cancel'))} />
          ) : null}
        </View>
      </Screen>

      {/* Mark held */}
      <Sheet visible={heldOpen} onClose={() => setHeldOpen(false)}>
        <SectionLabel>{t('mtd_who_present')}</SectionLabel>
        {m.kind === 'MC' ? (
          <View style={{ marginTop: space.s3 }}>
            {(m.committee ?? []).map((c) => {
              const on = present.has(c.id);
              return (
                <Pressable key={c.id} accessibilityRole="checkbox" accessibilityState={{ checked: on }}
                  onPress={() => setPresent((s) => {
                    const next = new Set(s);
                    if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                    return next;
                  })}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 10 }}>
                  <View style={{
                    width: 22, height: 22, borderRadius: 6, borderWidth: 1.5,
                    borderColor: on ? colors.primary500 : colors.border,
                    backgroundColor: on ? colors.primary500 : 'transparent',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    {on ? <Icon name="check" size={13} color={colors.canvas0} /> : null}
                  </View>
                  <AppText variant="bodyMd">{c.fullName}</AppText>
                </Pressable>
              );
            })}
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
              {t('mtd_quorum_needs', { q: m.quorumRequired ?? '—', total: (m.committee ?? []).length })}
            </AppText>
          </View>
        ) : (
          <Field label={t('mtd_members_present')} help={t('mtd_quorum_help', { n: m.quorumRequired ?? '—' })}>
            <Input value={headCount} onChangeText={setHeadCount} placeholder={t('mtd_ph_headcount')}
              keyboardType="number-pad" style={{ marginTop: space.s2 }} />
          </Field>
        )}
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3 }}>
          This is recorded provisionally — a second member certifies it with the minutes (bye-law 136).
        </AppText>
        <Button title={busy ? t('mtd_recording') : t('mtd_record_attendance')} disabled={busy} style={{ marginTop: space.s4 }}
          onPress={() => act('held',
            m.kind === 'MC' ? { presentUserIds: [...present] } : { presentCount: Number(headCount) || 0 },
            t('mtd_ok_attendance'))} />
      </Sheet>

      {/* Minutes */}
      <Sheet visible={minutesOpen} onClose={() => setMinutesOpen(false)}>
        <SectionLabel>{t('mtd_minutes_of_meeting')}</SectionLabel>
        <Input value={minutesText} onChangeText={setMinutesText} multiline
          placeholder={t('mtd_ph_minutes')}
          style={{ marginTop: space.s3, minHeight: 140, textAlignVertical: 'top' }} />
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
          Confirmed at the next meeting by someone other than the recorder — bye-law 136.
        </AppText>
        <Button title={busy ? t('mtd_saving') : t('mtd_save_minutes')} disabled={busy} style={{ marginTop: space.s4 }}
          onPress={() => act('minutes', { text: minutesText.trim() }, t('mtd_ok_minutes'))} />
      </Sheet>

      {/* Certify — the single second-person sign-off that covers attendance,
          quorum AND minutes (bye-law 136). Must be a different member from
          whoever recorded them; the API re-checks and cites the bye-law. */}
      <Sheet visible={certifyOpen} onClose={() => setCertifyOpen(false)}>
        <SectionLabel>{t('mtd_certify_the_meeting')}</SectionLabel>
        <AppText variant="bodySm" tone="secondary" style={{ marginTop: space.s2 }}>
          You&apos;re certifying, as a second member, that all of the following are correct
          (bye-law 136):
        </AppText>
        <View style={{ marginTop: space.s3, gap: space.s2 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
            <Icon name={m.quorumMet ? 'check' : 'x'} size={16}
              color={m.quorumMet ? colors.primary700 : colors.danger500} />
            <AppText variant="bodyMd" style={{ flex: 1 }}>
              {t('mtd_cert_attendance', { p: m.presentCount ?? 0, r: m.quorumRequired ?? 0, verdict: m.quorumMet ? t('mtd_met') : t('mtd_not_met') })}
            </AppText>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
            <Icon name="file-text" size={16} color={colors.textSecondary} />
            <AppText variant="bodyMd" style={{ flex: 1 }}>{t('mtd_cert_minutes')}</AppText>
          </View>
        </View>
        {!m.quorumMet ? (
          <AppText variant="bodySm" style={{ marginTop: space.s3, color: colors.danger500 }}>
            This meeting did not meet quorum — its resolutions are not valid and must be
            ratified at a properly constituted meeting.
          </AppText>
        ) : null}
        <Button title={busy ? t('mtd_certifying') : t('mtd_certify_full')} disabled={busy}
          style={{ marginTop: space.s4 }}
          onPress={() => { setCertifyOpen(false); act('minutes/confirm', {}, t('mtd_ok_certified')); }} />
      </Sheet>
    </Screen>
  );
}
